import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, ArrowRight, Sparkles, Lock, Baby, ShoppingCart, RefreshCw, Zap } from "lucide-react";
import { api } from "../lib/api";
import CheckoutModal from "../components/CheckoutModal";
import PaymentSuccessModal from "../components/PaymentSuccessModal";
import FemcareLogo from "../components/FemcareLogo";
import { useReveal, useSpotlight } from "../lib/motion";

export default function Pricing() {
    const { t } = useTranslation();
    const [plans, setPlans] = useState([]);
    const [faqs, setFaqs] = useState([]);
    const [open, setOpen] = useState(null);
    const [selectedPremium, setSelectedPremium] = useState(null);
    const [selectedAddon, setSelectedAddon] = useState(null);
    const [showCheckout, setShowCheckout] = useState(false);
    const [paidOrder, setPaidOrder] = useState(null);
    const [toast, setToast] = useState("");
    const [billingMode, setBillingMode] = useState("subscription"); // 'one-time' | 'subscription'

    const heroRef = useRef(null);
    useSpotlight(heroRef);
    useReveal([plans.length]);

    useEffect(() => {
        api.get("/pricing").then((r) => {
            setPlans(r.data);
            // Auto-select Premium Yearly by default (matches screenshot)
            const yearly = r.data.find((p) => p.name === "Premium Annual");
            if (yearly) setSelectedPremium(yearly);
        }).catch(console.error);
        api.get("/faqs").then((r) => setFaqs(r.data)).catch(console.error);
    }, []);

    const premiumMonthly = plans.find((p) => p.name === "Premium");
    const premiumYearly  = plans.find((p) => p.name === "Premium Annual");
    const addonMonthly   = plans.find((p) => p.name === "Daughter Care");
    const addonYearly    = plans.find((p) => p.name === "Daughter Care Annual");

    const showToast = (m) => { setToast(m); setTimeout(() => setToast(""), 3000); };

    const handleBuyNow = () => {
        if (!selectedPremium) {
            showToast("Please select Monthly or Yearly first.");
            return;
        }
        setShowCheckout(true);
    };

    const cart = [selectedPremium, selectedAddon].filter(Boolean);
    const cartTotal = cart.reduce((s, p) => s + parseInt(String(p.price).replace(/,/g, "") || "0", 10), 0);

    return (
        <div data-testid="pricing-page">
            {/* HERO — aurora + editorial */}
            <section ref={heroRef} className="relative overflow-hidden spotlight">
                <div className="aurora grain" aria-hidden="true" />
                <div className="max-w-5xl mx-auto px-6 lg:px-12 pt-20 pb-14 relative">
                    <div className="flex items-center gap-4 mb-8 reveal">
                        <div className="f-coin">
                            <FemcareLogo className="w-9 h-9" />
                        </div>
                        <span className="text-xs uppercase tracking-[0.35em] text-femcare-accent font-medium">
                            Premium Plans
                        </span>
                    </div>
                    <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-femcare-pink via-femcare-accent to-femcare-purple text-white p-10 md:p-14 text-center reveal reveal-delay-1" data-testid="premium-hero">
                        <div className="absolute inset-0 opacity-30">
                            <div className="absolute top-10 right-10 w-64 h-64 bg-white/10 rounded-full blur-3xl" />
                            <div className="absolute bottom-10 left-10 w-48 h-48 bg-white/10 rounded-full blur-3xl" />
                        </div>
                        <div className="relative">
                            <Sparkles className="w-12 h-12 mx-auto mb-6 text-white" fill="white" strokeWidth={0} />
                            <h1 className="editorial-headline text-5xl md:text-6xl lg:text-7xl font-medium mb-4">
                                Femcare Premium
                            </h1>
                            <p className="text-lg md:text-xl text-pink-100/90 max-w-2xl mx-auto leading-relaxed">
                                Advanced reports, AI insights &amp; beautiful PDF exports.
                            </p>
                            <div className="mt-6">
                                <Link
                                    to="/app/login"
                                    className="inline-flex items-center gap-2 text-sm text-white border border-white/40 hover:border-white rounded-full px-5 py-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm transition"
                                    data-testid="already-premium-link"
                                >
                                    Already Premium? Sign in
                                    <ArrowRight className="w-3.5 h-3.5" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* BILLING MODE TOGGLE */}
            <section className="max-w-5xl mx-auto px-6 lg:px-12 pb-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-femcare-petal/30 border border-femcare-border rounded-2xl p-4">
                    <p className="text-xs uppercase tracking-[0.2em] text-femcare-accent">{t("pricing.billingMode")}</p>
                    <div className="inline-flex bg-white border border-femcare-border rounded-full p-1" data-testid="billing-mode-toggle">
                        <button
                            onClick={() => setBillingMode("one-time")}
                            className={`px-5 py-2 rounded-full text-xs uppercase tracking-wider font-medium transition inline-flex items-center gap-1.5 ${billingMode === "one-time" ? "bg-femcare-purple text-white" : "text-femcare-purple/60"}`}
                            data-testid="billing-onetime"
                        >
                            <Zap className="w-3 h-3" /> {t("pricing.oneTime")}
                        </button>
                        <button
                            onClick={() => setBillingMode("subscription")}
                            className={`px-5 py-2 rounded-full text-xs uppercase tracking-wider font-medium transition inline-flex items-center gap-1.5 ${billingMode === "subscription" ? "bg-femcare-pink text-white" : "text-femcare-purple/60"}`}
                            data-testid="billing-subscription"
                        >
                            <RefreshCw className="w-3 h-3" /> {t("pricing.autoRenew")}
                        </button>
                    </div>
                </div>
            </section>

            {/* MONTHLY / YEARLY SELECTOR */}
            <section className="max-w-5xl mx-auto px-6 lg:px-12 pb-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {[premiumMonthly, premiumYearly].map((p, i) => {
                        if (!p) return null;
                        const isYearly = p.name === "Premium Annual";
                        const isSelected = selectedPremium?.id === p.id;
                        const monthlyEquiv = isYearly ? Math.round(parseInt(p.price) / 12) : null;

                        return (
                            <button
                                key={p.id}
                                onClick={() => setSelectedPremium(p)}
                                className={`relative text-left rounded-3xl p-8 md:p-10 transition-all bg-white border-2 ${
                                    isSelected
                                        ? "border-femcare-pink shadow-xl shadow-femcare-pink/15"
                                        : "border-femcare-border hover:border-femcare-pink/50"
                                }`}
                                data-testid={`premium-${isYearly ? "yearly" : "monthly"}`}
                            >
                                {isYearly && (
                                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-femcare-accent text-white text-xs uppercase tracking-[0.2em] px-4 py-1.5 rounded-full">
                                        72% off
                                    </div>
                                )}

                                <p className="text-xs uppercase tracking-[0.3em] text-femcare-purple/70 text-center mb-4 font-medium">
                                    {isYearly ? "Yearly" : "Monthly"}
                                </p>

                                <div className="text-center">
                                    <div className="font-serif font-medium text-femcare-purple leading-none mb-2">
                                        <span className="text-3xl align-top">₹</span>
                                        <span className="text-6xl md:text-7xl">{p.price}</span>
                                    </div>
                                    <p className="text-femcare-purple/60">per {p.period}</p>
                                    {isYearly && monthlyEquiv && (
                                        <p className="text-sm text-femcare-accent mt-1">
                                            ≈ ₹{monthlyEquiv}/mo
                                        </p>
                                    )}
                                </div>

                                {/* Selection circle / checkmark */}
                                <div className="flex justify-center mt-8">
                                    <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition ${
                                        isSelected
                                            ? "bg-femcare-pink border-femcare-pink"
                                            : "border-femcare-border bg-white"
                                    }`}>
                                        {isSelected && <Check className="w-5 h-5 text-white" strokeWidth={3} />}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>

                {/* What's included */}
                {selectedPremium && (
                    <div className="mt-8 bg-femcare-petal/40 border border-femcare-border rounded-2xl p-6">
                        <p className="text-xs uppercase tracking-[0.3em] text-femcare-accent mb-3">What's included</p>
                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2.5">
                            {selectedPremium.features.map((f) => (
                                <li key={f} className="flex gap-3 text-sm text-femcare-purple/80">
                                    <Check className="w-4 h-4 mt-0.5 shrink-0 text-femcare-pink" />
                                    <span>{f}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </section>

            {/* ADD DAUGHTER CARE */}
            <section className="max-w-5xl mx-auto px-6 lg:px-12 pb-10">
                <div className="flex items-center gap-3 mb-6">
                    <Baby className="w-5 h-5 text-femcare-pink" />
                    <h2 className="font-serif text-2xl md:text-3xl text-femcare-purple uppercase tracking-wider">
                        Add Daughter Care
                    </h2>
                </div>
                {!selectedPremium && (
                    <div className="bg-femcare-muted border border-femcare-border rounded-xl p-4 mb-6 flex items-center gap-3 text-sm text-femcare-purple/70" data-testid="daughter-care-locked-banner">
                        <Lock className="w-4 h-4 text-femcare-accent" />
                        Select Premium above to unlock Daughter Care add-on.
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {[addonMonthly, addonYearly].map((p) => {
                        if (!p) return null;
                        const isYearly = p.period === "year";
                        const locked = !selectedPremium;
                        const isSelected = selectedAddon?.id === p.id;

                        return (
                            <button
                                key={p.id}
                                onClick={() => {
                                    if (locked) {
                                        showToast("Please select a Premium plan first.");
                                        return;
                                    }
                                    setSelectedAddon(isSelected ? null : p);
                                }}
                                disabled={locked}
                                className={`relative text-left rounded-3xl p-7 transition-all bg-white border-2 ${
                                    locked
                                        ? "border-femcare-border opacity-60 cursor-not-allowed"
                                        : isSelected
                                            ? "border-femcare-pink shadow-xl shadow-femcare-pink/15"
                                            : "border-femcare-border hover:border-femcare-pink/50"
                                }`}
                                data-testid={`addon-${isYearly ? "yearly" : "monthly"}`}
                            >
                                <div className="inline-block bg-femcare-accent/20 text-femcare-accent text-xs uppercase tracking-[0.2em] px-3 py-1 rounded-full mb-4">
                                    {isYearly ? "Yearly add-on" : "Monthly add-on"}
                                </div>

                                <h3 className="font-serif text-xl text-femcare-purple uppercase tracking-wider leading-tight mb-4">
                                    Daughter Care<br />Add-on{isYearly ? " (Yearly)" : " (Monthly)"}
                                </h3>

                                <div className="font-serif font-medium text-femcare-purple leading-none mb-2">
                                    <span className="text-2xl align-top">₹</span>
                                    <span className="text-5xl">{p.price}</span>
                                </div>
                                <p className="text-femcare-purple/60 text-sm mb-5">per {p.period}</p>

                                <ul className="space-y-2 mb-5">
                                    {p.features.slice(0, 3).map((f) => (
                                        <li key={f} className="flex gap-2 text-xs text-femcare-purple/70">
                                            <Check className="w-3.5 h-3.5 mt-0.5 shrink-0 text-femcare-pink" />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>

                                {locked ? (
                                    <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-femcare-purple/40">
                                        <Lock className="w-3.5 h-3.5" /> Requires Premium
                                    </div>
                                ) : (
                                    <div className={`flex items-center justify-center w-8 h-8 rounded-full ${
                                        isSelected ? "bg-femcare-pink" : "border-2 border-femcare-border bg-white"
                                    }`}>
                                        {isSelected && <Check className="w-4 h-4 text-white" strokeWidth={3} />}
                                    </div>
                                )}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* BUY NOW BAR */}
            <section className="max-w-5xl mx-auto px-6 lg:px-12 pb-20">
                <div className="bg-femcare-purple text-white rounded-3xl p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5" data-testid="buy-now-bar">
                    <div>
                        <p className="text-xs uppercase tracking-[0.3em] text-pink-100/70 mb-2">Your selection</p>
                        <div className="flex flex-wrap gap-2 items-baseline">
                            <span className="font-serif text-3xl md:text-4xl">₹{cartTotal.toLocaleString("en-IN")}</span>
                            <span className="text-pink-100/70 text-sm">
                                {selectedPremium ? `· ${selectedPremium.name}` : "· Choose a plan"}
                                {selectedAddon ? ` + Daughter Care` : ""}
                            </span>
                        </div>
                    </div>
                    <button
                        onClick={handleBuyNow}
                        disabled={!selectedPremium}
                        className="bg-femcare-pink hover:bg-femcare-rose disabled:opacity-50 disabled:cursor-not-allowed rounded-full px-8 py-4 font-medium inline-flex items-center justify-center gap-2 transition"
                        data-testid="buy-now-btn"
                    >
                        <ShoppingCart className="w-4 h-4" /> Buy Now
                        <ArrowRight className="w-4 h-4" />
                    </button>
                </div>
            </section>

            {/* Checkout & Success modals */}
            {showCheckout && cart.length > 0 && (
                <CheckoutModal
                    cart={cart}
                    billingMode={billingMode}
                    onClose={() => setShowCheckout(false)}
                    onSuccess={(order) => {
                        setShowCheckout(false);
                        setPaidOrder(order);
                        setSelectedAddon(null);
                    }}
                />
            )}
            {paidOrder && <PaymentSuccessModal order={paidOrder} onClose={() => setPaidOrder(null)} />}
            {toast && (
                <div className="fixed top-24 right-6 z-50 bg-femcare-purple text-white px-5 py-3 rounded-2xl shadow-xl text-sm fade-up max-w-sm" data-testid="pricing-toast">
                    {toast}
                </div>
            )}

            {/* FAQ */}
            <section className="max-w-4xl mx-auto px-6 lg:px-12 py-16 border-t border-femcare-border">
                <h2 className="editorial-headline text-4xl md:text-5xl text-femcare-purple text-center mb-12">
                    Frequently asked.
                </h2>
                <div className="space-y-3">
                    {faqs.map((f, i) => (
                        <div
                            key={f.id}
                            className="bg-white border border-femcare-border rounded-2xl overflow-hidden"
                            data-testid={`faq-${i}`}
                        >
                            <button
                                onClick={() => setOpen(open === i ? null : i)}
                                className="w-full p-6 text-left flex justify-between items-center gap-4"
                            >
                                <span className="font-serif text-lg text-femcare-purple">{f.question}</span>
                                <span className={`text-femcare-pink text-2xl transition-transform ${open === i ? 'rotate-45' : ''}`}>+</span>
                            </button>
                            {open === i && (
                                <div className="px-6 pb-6 text-femcare-purple/70 leading-relaxed">{f.answer}</div>
                            )}
                        </div>
                    ))}
                </div>
            </section>
        </div>
    );
}
